// HTTP adapter for the survey-api surface (ADR-0056).

import type {
  Campaign,
  ItemPair,
  LemmaMeta,
  PairRatingResult,
  PairRatingSubmission,
  RatingResult,
  RatingSubmission,
  SignalementDecision,
  SignalementHistoryItem,
  SignalementInput,
  SignalementSummary,
  SurveyClient,
  SurveyContribution,
  SurveyItem,
  SurveyPreferencesPatch,
  SurveyProgress,
} from '@/application/survey';
import { ContribuerForbiddenError, ReportRateLimitedError } from '@/application/survey';
import type { components, paths } from './types';

type CorrectifRejection = components['schemas']['CorrectifRejection'];
type CampaignResponse = components['schemas']['Campaign'];

export class SignInRequiredError extends Error {
  constructor() {
    super('sign in required');
    this.name = 'SignInRequiredError';
  }
}

export class CorrectifRejectedError extends Error {
  readonly detail: CorrectifRejection;
  constructor(detail: CorrectifRejection) {
    super(`correctif rejected by filter ${detail.filterId}: ${detail.reason}`);
    this.name = 'CorrectifRejectedError';
    this.detail = detail;
  }
}

export class AlreadyRatedError extends Error {
  readonly response: RatingResult;
  constructor(response: RatingResult) {
    super('already rated');
    this.name = 'AlreadyRatedError';
    this.response = response;
  }
}

export class SondageLockedError extends Error {
  constructor() {
    super('sondage locked');
    this.name = 'SondageLockedError';
  }
}

export class NoCampaignError extends Error {
  constructor() {
    super('no campaign');
    this.name = 'NoCampaignError';
  }
}

export class UndoExpiredError extends Error {
  constructor() {
    super('undo window expired');
    this.name = 'UndoExpiredError';
  }
}

export class UndoUnavailableError extends Error {
  constructor() {
    super('undo unavailable');
    this.name = 'UndoUnavailableError';
  }
}

export interface HttpSurveyClientOptions {
  readonly baseUrl: string;
  readonly fetch?: typeof globalThis.fetch;
}

export function createHttpSurveyClient(options: HttpSurveyClientOptions): SurveyClient {
  // Resolve fetch at call time so MSW `.listen()` interception takes effect.
  const fetchImpl: typeof globalThis.fetch = options.fetch
    ? options.fetch
    : (...args) => globalThis.fetch(...args);
  const base = options.baseUrl.replace(/\/$/, '');

  const getNextItem: SurveyClient['getNextItem'] = async (opts = {}) => {
    const params = new URLSearchParams();
    const excluded = opts.excludedItemIds;
    if (excluded && excluded.length > 0) params.set('excluded', excluded.join(','));
    const query = params.toString();
    const url = `${base}/v1/items/next${query ? `?${query}` : ''}`;
    const res = await fetchImpl(url, { credentials: 'include' });
    if (res.status === 204) return null;
    if (!res.ok) throw new Error(`getNextItem failed: ${res.status}`);
    return (await res.json()) as SurveyItem;
  };

  const submitRating: SurveyClient['submitRating'] = async (itemId: string, body: RatingSubmission) => {
    const url = `${base}/v1/items/${encodeURIComponent(itemId)}/rating`;
    const res = await fetchImpl(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 401) throw new SignInRequiredError();
    if (res.status === 422) {
      const detail = (await res.json()) as CorrectifRejection;
      throw new CorrectifRejectedError(detail);
    }
    if (res.status === 409) {
      // Auth caller already rated this item; the response envelope is the existing rating.
      throw new AlreadyRatedError((await res.json()) as RatingResult);
    }
    if (res.status === 423) throw new SondageLockedError();
    if (!res.ok) throw new Error(`submitRating failed: ${res.status}`);
    return (await res.json()) as RatingResult;
  };

  const getNextPair: SurveyClient['getNextPair'] = async (opts = {}) => {
    const params = new URLSearchParams();
    const excluded = opts.excludedItemIds;
    if (excluded && excluded.length > 0) params.set('excluded', excluded.join(','));
    const query = params.toString();
    const url = `${base}/v1/items/pairs/next${query ? `?${query}` : ''}`;
    const res = await fetchImpl(url, { credentials: 'include' });
    if (res.status === 204) return null;
    if (!res.ok) throw new Error(`getNextPair failed: ${res.status}`);
    return (await res.json()) as ItemPair;
  };

  const submitPairRating: SurveyClient['submitPairRating'] = async (body: PairRatingSubmission) => {
    const res = await fetchImpl(`${base}/v1/ratings/pair`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 401) throw new SignInRequiredError();
    // 409 means the auth caller already rated this pair; surface as AlreadyRatedError without a payload body.
    if (res.status === 409) {
      throw new AlreadyRatedError({
        ratingId: '',
        itemId: body.leftItemId,
        submittedAs: 'auth',
        proposedItemId: null,
        undoToken: null,
      });
    }
    if (res.status === 423) throw new SondageLockedError();
    if (!res.ok) throw new Error(`submitPairRating failed: ${res.status}`);
    // SKIP verdicts return 204 with no body; no undo token to surface.
    if (res.status === 204) return { undoToken: null };
    const json = (await res.json()) as { undoToken?: string | null };
    return { undoToken: json.undoToken ?? null } satisfies PairRatingResult;
  };

  const undoAction: SurveyClient['undoAction'] = async (token: string) => {
    const res = await fetchImpl(`${base}/v1/actions/undo`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (res.status === 204) return;
    if (res.status === 404) throw new UndoUnavailableError();
    if (res.status === 410) throw new UndoExpiredError();
    throw new Error(`undoAction failed: ${res.status}`);
  };

  const getCurrentCampaign: SurveyClient['getCurrentCampaign'] = async () => {
    const res = await fetchImpl(`${base}/v1/campaign/current`, { credentials: 'include' });
    if (res.status === 503) throw new NoCampaignError();
    if (!res.ok) throw new Error(`getCurrentCampaign failed: ${res.status}`);
    const body = (await res.json()) as CampaignResponse;
    return {
      campaignId: body.campaignId,
      batchLabel: body.batchLabel,
      openedAt: body.openedAt,
      closedAt: body.closedAt,
    } satisfies Campaign;
  };

  const getProgress: SurveyClient['getProgress'] = async () => {
    const res = await fetchImpl(`${base}/v1/me/progress`, { credentials: 'include' });
    if (res.status === 401) throw new SignInRequiredError();
    if (!res.ok) throw new Error(`getProgress failed: ${res.status}`);
    return (await res.json()) as SurveyProgress;
  };

  const getContributions: SurveyClient['getContributions'] = async () => {
    const res = await fetchImpl(`${base}/v1/me/contributions`, { credentials: 'include' });
    if (res.status === 401) throw new SignInRequiredError();
    if (!res.ok) throw new Error(`getContributions failed: ${res.status}`);
    return (await res.json()) as SurveyContribution[];
  };

  const getLemmaMeta: SurveyClient['getLemmaMeta'] = async (mot: string) => {
    const res = await fetchImpl(`${base}/v1/lemma-meta/${encodeURIComponent(mot)}`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`getLemmaMeta failed: ${res.status}`);
    return (await res.json()) as LemmaMeta;
  };

  const patchPreferences: SurveyClient['patchPreferences'] = async (body: SurveyPreferencesPatch) => {
    const res = await fetchImpl(`${base}/v1/me/preferences`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 401) throw new SignInRequiredError();
    if (!res.ok) throw new Error(`patchPreferences failed: ${res.status}`);
  };

  const submitSignalement: SurveyClient['submitSignalement'] = async (input: SignalementInput) => {
    const body: components['schemas']['SignalementRequest'] = {
      clueText: input.clueText,
      reason: input.reason,
      surface: input.surface,
      ...(input.note ? { note: input.note } : {}),
      ...(input.puzzleId ? { puzzleId: input.puzzleId } : {}),
    };
    const res = await fetchImpl(`${base}/v1/signalements`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 429) throw new ReportRateLimitedError();
    if (!res.ok) throw new Error(`submitSignalement failed: ${res.status}`);
    // 201 for both a fresh accept and a server-side duplicate — same envelope either way.
    const json = (await res.json()) as components['schemas']['SignalementResponse'];
    return { reportId: json.reportId };
  };

  const listSignalements: SurveyClient['listSignalements'] = async () => {
    const res = await fetchImpl(`${base}/v1/signalements`, { credentials: 'include' });
    if (res.status === 403) throw new ContribuerForbiddenError();
    if (!res.ok) throw new Error(`listSignalements failed: ${res.status}`);
    const json = (await res.json()) as components['schemas']['SignalementListResponse'];
    return json.items.map(
      (it): SignalementSummary => ({
        reportId: it.reportId,
        wordText: it.wordText,
        clueText: it.clueText,
        reason: it.reason,
        surface: it.surface,
        puzzleId: it.puzzleId,
        count: it.count,
        latestNote: it.latestNote,
        latestAt: it.latestAt,
        mine: it.mine,
      }),
    );
  };

  const listHandledSignalements: SurveyClient['listHandledSignalements'] = async () => {
    const res = await fetchImpl(`${base}/v1/signalements/historique`, { credentials: 'include' });
    if (res.status === 403) throw new ContribuerForbiddenError();
    if (!res.ok) throw new Error(`listHandledSignalements failed: ${res.status}`);
    const json = (await res.json()) as components['schemas']['SignalementHistoryResponse'];
    return json.items.map(
      (it): SignalementHistoryItem => ({
        reportId: it.reportId,
        wordText: it.wordText,
        clueText: it.clueText,
        reason: it.reason,
        surface: it.surface,
        puzzleId: it.puzzleId,
        note: it.note,
        decision: it.decision,
        triagedAt: it.triagedAt,
      }),
    );
  };

  const decideSignalement: SurveyClient['decideSignalement'] = async (
    reportId: string,
    decision: SignalementDecision,
  ) => {
    const res = await fetchImpl(`${base}/v1/signalements/${encodeURIComponent(reportId)}/decision`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ decision } satisfies components['schemas']['SignalementDecisionRequest']),
    });
    if (res.status === 403) throw new ContribuerForbiddenError();
    if (res.status === 204) return;
    if (!res.ok) throw new Error(`decideSignalement failed: ${res.status}`);
  };

  const undoSignalement: SurveyClient['undoSignalement'] = async (reportId: string) => {
    const res = await fetchImpl(`${base}/v1/signalements/${encodeURIComponent(reportId)}/undo`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.status === 403) throw new ContribuerForbiddenError();
    if (res.status === 204) return;
    if (!res.ok) throw new Error(`undoSignalement failed: ${res.status}`);
  };

  return {
    getNextItem,
    submitRating,
    getNextPair,
    submitPairRating,
    undoAction,
    getProgress,
    getContributions,
    patchPreferences,
    getCurrentCampaign,
    getLemmaMeta,
    submitSignalement,
    listSignalements,
    listHandledSignalements,
    decideSignalement,
    undoSignalement,
  };
}

export type { paths };
